/**
 * ModalGiros — todos los giros del día en un solo paso.
 *
 * QUÉ REEMPLAZA. El paso 6 pedía las cosas con `window.prompt`: uno para la referencia,
 * otro para la justificación, y había que volver a apretar el botón por cada compañía. Con
 * tres compañías eso son seis diálogos del navegador encadenados, sin ver los montos
 * mientras se responden, sin poder corregir el anterior y sin poder cancelar a la mitad sin
 * dejar el día hecho a medias. Y un `prompt` no se puede diseñar: no muestra a quién se le
 * está girando ni cuánto.
 *
 * UNA COMPAÑÍA POR FILA, UN CAMPO POR TRANSFERENCIA. No se puede pedir una sola referencia
 * para todas: cada giro es una transferencia distinta en el banco, con su propio número, y
 * ése es el dato que después cruza contra la cartola. Lo que sí se puede es pedirlas todas
 * juntas, viendo el monto de cada una.
 *
 * LA JUSTIFICACIÓN SE PIDE UNA VEZ, no una por compañía. La condición es del DÍA —la misma
 * persona aceptó el depósito— así que preguntarla tres veces es preguntar tres veces lo
 * mismo.
 *
 * SE PUEDE GIRAR A ALGUNAS Y NO A TODAS. Cada fila se marca: si hoy sólo salieron dos de
 * las tres transferencias, se registran esas dos. Obligar a completar el día empujaría a
 * inventar la referencia que falta.
 *
 * EL RESULTADO ES POR COMPAÑÍA. Si una falla, las otras quedan registradas y el error se
 * muestra en SU fila. Un error global obligaría a adivinar cuál falló y a reintentar las
 * que ya habían salido.
 *
 * @see src/pages/payments/yoolpay/YoolPayDia.jsx — quién lo abre y qué hace al confirmar
 */
import React, { useMemo, useState } from 'react';

import { Boton, Input, Modal, TextArea } from './cierre/ds';
import { CIFRA } from '../../../lib/yoolpay/estados';
import { fmtClp } from '../../../lib/yoolpay/formato';

const MINIMO_JUSTIFICACION = 8;
const COLS = '28px 1fr 120px 1.1fr';

/** Lo que escribió el usuario, venga como evento o como valor suelto según el DS. */
const valorDe = (e) => (e && e.target ? e.target.value : e) ?? '';

export default function ModalGiros({
  abierto, pendientes = [], permiso, cargando, resultados, onConfirmar, onCerrar,
}) {
  // La referencia de cada transferencia. Arranca vacía: teclearla ES el acto de afirmar
  // que esa transferencia existe.
  const [refs, setRefs] = useState({});
  // Todas marcadas al abrir: el caso normal es girarle a todas las que faltan.
  const [desmarcadas, setDesmarcadas] = useState({});
  const [justificacion, setJustificacion] = useState('');

  const pideRazon = permiso?.requiere_justificacion === true;
  const marcada = (id) => desmarcadas[id] !== true;

  const elegidas = useMemo(
    () => pendientes.filter((p) => desmarcadas[p.compania_id] !== true),
    [pendientes, desmarcadas],
  );

  // Qué falta para poder confirmar. Se calcula acá y no dentro del botón para poder
  // DECIRLO: un botón deshabilitado sin explicación no dice qué le falta.
  const falta = useMemo(() => {
    if (elegidas.length === 0) return 'Marca al menos una compañía.';
    const sinRef = elegidas.filter((p) => !(refs[p.compania_id] ?? '').trim());
    if (sinRef.length > 0) {
      return sinRef.length === 1
        ? `Falta el número de la transferencia de ${sinRef[0].compania_nombre ?? 'una compañía'}.`
        : `Faltan ${sinRef.length} números de transferencia.`;
    }
    if (pideRazon && justificacion.trim().length < MINIMO_JUSTIFICACION) {
      return `La razón necesita al menos ${MINIMO_JUSTIFICACION} caracteres.`;
    }
    return null;
  }, [elegidas, refs, pideRazon, justificacion]);

  const total = elegidas.reduce((s, p) => s + Number(p.monto_clp ?? 0), 0);

  return (
    <Modal
      open={abierto}
      onClose={cargando ? undefined : onCerrar}
      eyebrow="Paso 6"
      title="Registrar los giros del día"
      desc={
        'Cada transferencia se registra con su propio número, que es lo que después cruza '
        + 'contra la cartola. Marca las que ya salieron del banco.'
      }
      size="lg"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* LA RAZÓN, ARRIBA Y UNA SOLA VEZ. Es del día, no de cada compañía. */}
        {pideRazon ? (
          <span className="cw-aviso media" style={{ flexDirection: 'column', gap: 9 }}>
            <span style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
              <span className="cw-ai"><span>!</span></span>
              <span>{permiso.motivo}</span>
            </span>
            <TextArea
              rows={2}
              value={justificacion}
              onChange={(e) => setJustificacion(valorDe(e))}
              placeholder="Por qué no hubo una segunda persona"
            />
          </span>
        ) : null}

        <div className="cw-card" style={{ borderRadius: 12 }}>
          <div className="cw-row cw-head-row" style={{ gridTemplateColumns: COLS, padding: '9px 16px' }}>
            <span />
            <span className="cw-th">Compañía</span>
            <span className="cw-th" style={{ textAlign: 'right' }}>Monto</span>
            <span className="cw-th">N° de transferencia</span>
          </div>

          {pendientes.map((p, i) => {
            const r = resultados?.[p.compania_id];
            const on = marcada(p.compania_id);
            const bloqueada = cargando || r?.ok === true;
            return (
              <div
                key={p.compania_id}
                className={`cw-row${r?.ok ? ' done' : ''}${r?.error ? ' obs' : ''}`
                  + `${i === pendientes.length - 1 ? ' last' : ''}`}
                style={{ gridTemplateColumns: COLS, padding: '11px 16px', alignItems: 'center' }}
              >
                <button
                  type="button"
                  onClick={() => setDesmarcadas((m) => ({ ...m, [p.compania_id]: on }))}
                  disabled={bloqueada}
                  aria-label={`Incluir ${p.compania_nombre ?? p.compania_id}`}
                  className={`cw-chk${on ? ' on' : ''}`}
                  style={{ cursor: bloqueada ? 'default' : 'pointer' }}
                >
                  {on ? '✓' : ''}
                </button>

                <span style={{ minWidth: 0 }}>
                  <span
                    title={p.compania_id}
                    style={{
                      display: 'block', fontSize: 13, fontWeight: 600,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {p.compania_nombre ?? p.compania_id}
                  </span>
                  {/* EL RESULTADO VA EN SU FILA: si una falla se ve cuál y por qué, y las
                      otras quedan registradas sin tener que reintentarlas. */}
                  {r?.error ? (
                    <span style={{ display: 'block', fontSize: 11.5, color: CIFRA.rojo, marginTop: 2 }}>
                      {r.error}
                    </span>
                  ) : null}
                  {r?.ok ? (
                    <span style={{ display: 'block', fontSize: 11.5, color: CIFRA.verde, marginTop: 2 }}>
                      Registrado{r.codigo ? ` · ${r.codigo}` : ''}
                    </span>
                  ) : null}
                </span>

                <span
                  className="cw-mono"
                  style={{ fontSize: 13, fontWeight: 600, textAlign: 'right' }}
                >
                  {fmtClp(p.monto_clp)}
                </span>

                <Input
                  value={refs[p.compania_id] ?? ''}
                  onChange={(e) => {
                    const v = valorDe(e);
                    setRefs((s) => ({ ...s, [p.compania_id]: v }));
                  }}
                  placeholder="Ej. 1234567"
                  disabled={bloqueada || !on}
                />
              </div>
            );
          })}
        </div>

        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 14, flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 12.5, color: falta ? CIFRA.ambar : 'var(--yool-gray1)' }}>
            {falta ?? `${elegidas.length} ${elegidas.length === 1 ? 'giro' : 'giros'} `
              + `por ${fmtClp(total)}`}
          </span>
          <span style={{ display: 'flex', gap: 9 }}>
            <Boton variant="ghost" onClick={onCerrar} disabled={cargando}>Cerrar</Boton>
            <Boton
              variant="primary"
              disabled={falta != null || cargando}
              onClick={() => onConfirmar(
                elegidas.map((p) => ({
                  compania_id: p.compania_id,
                  compania_nombre: p.compania_nombre,
                  monto_clp: p.monto_clp,
                  referencia: (refs[p.compania_id] ?? '').trim(),
                })),
                pideRazon ? justificacion.trim() : undefined,
              )}
            >
              {cargando ? 'Registrando…' : 'Registrar giros'}
            </Boton>
          </span>
        </div>
      </div>
    </Modal>
  );
}
